import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, Table, TabPanel, Tabs, Icon } from 'app/components';
import { useLazyGetUserNotificationsQuery } from 'app/api/facade';
import { NotificationsSettings } from './NotificationsSettings';
import { columns } from './columns';

import { DeleteNtfRuleModal } from 'app/components/DeleteNtfRuleModal';
import styles from './NotificationsPage.module.scss';
import { CreateNotificationModal } from './Modals/CreateNotificationModal';
import { CreateVesselRulesModal } from './Modals/CreateVesselRulesModal';
import { ComponetnSettingT } from './types';

export enum AccessorAndParent {
  Vessel = 'vessel',
  Port = 'port',
  Area = 'area',
  Anchorage = 'anchorage',
  TargetSafety = 'targetSafety'
}
export const NotificationsPage = () => {
  const { t } = useTranslation();
  const [getNotifications, { ...notificationsState }] = useLazyGetUserNotificationsQuery();
  const showLoader = notificationsState.isLoading || notificationsState.isFetching;
  const deleteNtfRuleModalState = useState<boolean>(false);
  const createNotificationModalState = useState<boolean>(false);
  const createNotificationRuleState = useState<ComponetnSettingT>({
    show: false,
    type: '',
    accessor: null,
    parent: null,
  });

  const tabs = [
    {
      value: 'notifications-list',
      label: t('NotificationsList'),
    },
    {
      value: 'notifications-settings',
      label: t('NotificationsSettings'),
    },
  ];

  const [tabValue, setTabValue] = useState<string>();
  const [date, setDate] = useState(new Date().toString());
  const [shouldUpdate, setShouldUpdate] = useState(false);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleScroll = (event: WheelEvent) => {
    const target = event.currentTarget as HTMLElement;
    const lastItem =
      notificationsState.data?.notifications && notificationsState.data.notifications?.length !== 0
        ? [...notificationsState.data.notifications].pop()
        : null;

    setShouldUpdate(target?.scrollHeight - target?.scrollTop === target.offsetHeight && lastItem !== null);
    setDate(lastItem?.dt ? lastItem.dt : date);
  };

  useEffect(() => {
    if (!tabValue) {
      setTabValue(tabs[0]?.value);
    }
  }, [tabs]);

  useEffect(() => {
    getNotifications({ datetime: '..' });
  }, []);

  useEffect(() => {
    if (shouldUpdate) {
      setShouldUpdate((prev) => !prev);
      getNotifications({ datetime: date });
    }
  }, [shouldUpdate]);

  return (
    <div className={styles.page}>
      <PageHeader iconName="notifications" title={t('Notifications')} />
      <div className={styles.tables}>
        <Tabs tabs={tabs} value={tabValue} onChange={handleChange} />
        <div className={styles.notifications_tab_panel_wrapper}>
          <TabPanel
            id="notifications-list"
            value={tabValue}
            onScroll={(event: WheelEvent) => handleScroll(event)}
            className={styles.notifications_tab_panel}
          >
            {!notificationsState.currentData?.notifications && showLoader && (
              <div className={styles.notifications_loader}>
                <Icon name="spinner-light" size="16" color={`var(--default-primary)`} className={styles.spinner_animate} />
                {t('DataIsLoading')}
              </div>
            )}
            {notificationsState.currentData?.notifications?.length === 0 && (
              <div className={styles.notifications_empty}>{t('NoNotifications')}</div>
            )}

            {notificationsState.currentData?.notifications && notificationsState.currentData?.notifications?.length !== 0 && (
              <>
                <div className={styles.table_container}>
                  <Table manualSort={true} columns={columns} data={notificationsState.currentData?.notifications} />
                </div>
                {showLoader && (
                  <div className={styles.loader}>
                    <Icon name="spinner-light" size="16" color={`var(--default-primary)`} className={styles.spinner_animate} />
                    {t('DataIsLoading')}
                  </div>
                )}
              </>
            )}
          </TabPanel>
          <TabPanel id="notifications-settings" value={tabValue} className={styles.notifications_tab_panel}>
            <NotificationsSettings
              deleteNtfRuleModalState={deleteNtfRuleModalState}
              createNotificationModalState={createNotificationModalState}
            />
          </TabPanel>
        </div>
      </div>
      <DeleteNtfRuleModal modalState={deleteNtfRuleModalState} />
      <CreateNotificationModal
        createNotificationModalState={createNotificationModalState}
        createNotificationRuleState={createNotificationRuleState}
      />
      <CreateVesselRulesModal showCreateVesselArrivalPortRuleState={createNotificationRuleState} />
    </div>
  );
};
